import { createAction, handleActions, Action } from 'redux-actions';
import { useRedux } from 'util/hook/redux';
import { Dispatch } from 'redux';
import history from 'store/history';

import { api } from 'util/api';
import { EyesHeReportListRequestParams } from 'util/api/swaggerApi/data-contracts';

import { updateAccessToken } from './auth';
import {
	processEyesImageReportList,
	EyeCheckImageReportType,
	EyesImageReportModalityItem,
} from './eyeCheckImageReport';

import { State as GlobalState } from './reducers';

export type { EyeCheckImageReportType as AdultCheckupHeReportType };
export type { MediaType } from './eyeCheckImageReport';

// Note: The old EyesHeReportApiItem interface and helper functions are no longer needed
// as the API now returns data in a different structure (data.data.aps.record as an object)

const useEyesHeReportMock = (): boolean =>
	process.env.USE_EYES_HE_REPORT_MOCK === true || process.env.USE_EYES_IMAGE_REPORT_MOCK === true;

export const getAdultCheckUpHeReportList = createAction(
	'GET_ADULT_CHECKUP_HE_REPORT',
	(eyesHeType: string) =>
		async (dispatch: Dispatch) => {
			if (useEyesHeReportMock()) {
				const { eyesHeReportMockModalityItems } = await import(
					/* webpackChunkName: 'eyesHeReportMock' */ 'models/mocks/eyesHeReportMock'
				);
				return processEyesImageReportList(eyesHeReportMockModalityItems);
			}

			const params: EyesHeReportListRequestParams = {
				eye_image_type: eyesHeType,
			};

			let response;
			const { v1UserEyesHeReportList } = api;

			try {
				response = await v1UserEyesHeReportList(params);
			} catch (e) {
				console.log('adult checkup he report error', e);
				const { status } = e as { status: number };
				if (status === 401) {
					dispatch(updateAccessToken(''));
					history.push('/');
				}
				return [];
			}

			const { data } = response;

			// New API response structure: data.data contains aps directly
			const apiData = data?.data as {
				aps?: {
					url?: string;
					record?: Record<string, any[] | null>;
				};
			};

			if (!apiData?.aps?.record) {
				return [];
			}

			// Transform the new response structure to modality items
			const modalityItems: EyesImageReportModalityItem[] = [];
			const apsData = apiData.aps;
			const records = apsData.record;

			// Convert record object to modality items array
			Object.entries(records).forEach(([modality, recordList]) => {
				if (recordList && Array.isArray(recordList)) {
					modalityItems.push({
						modality,
						data: {
							aps: {
								url: apsData.url,
								record: recordList,
							},
						},
					});
				}
			});

			return processEyesImageReportList(modalityItems);
		},
);

/** @deprecated Use getAdultCheckUpHeReportList */
export const getAdultCheckUpReportList = getAdultCheckUpHeReportList;

export interface State {
	loading: boolean;
	reports: EyeCheckImageReportType[];
}

export const defaultState: State = {
	loading: false,
	reports: [],
};

export const reducer = {
	adultCheckUpReports: handleActions<State, EyeCheckImageReportType[]>(
		{
			GET_ADULT_CHECKUP_HE_REPORT_PENDING: state => ({
				...state,
				loading: true,
			}),
			GET_ADULT_CHECKUP_HE_REPORT_FULFILLED: (state, action: Action<EyeCheckImageReportType[]>) => ({
				...state,
				reports: action.payload,
				loading: false,
			}),
			GET_ADULT_CHECKUP_HE_REPORT_REJECTED: state => ({
				...state,
				loading: false,
			}),
		},
		defaultState,
	),
};

const adultCheckUpReportActionsMap = {
	getAdultCheckUpHeReportList,
	getAdultCheckUpReportList,
};

const mapHooksToState = (state: GlobalState) => ({
	adultCheckUpReports: state.adultCheckUpReports.reports,
	loading: state.adultCheckUpReports.loading,
});

type AdultCheckUpReportSelector = ReturnType<typeof mapHooksToState>;
type AdultCheckUpReportActionsMap = typeof adultCheckUpReportActionsMap;

export const useAdultCheckUpReport = () =>
	useRedux<AdultCheckUpReportSelector, AdultCheckUpReportActionsMap>(mapHooksToState, adultCheckUpReportActionsMap);
